import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addDays, format, parseISO } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { sendOwnerPush } from "@/lib/owner-push";

const DOW_ALL: Array<{ n: number; label: string }> = [
  { n: 1, label: "Mon" },
  { n: 2, label: "Tue" },
  { n: 3, label: "Wed" },
  { n: 4, label: "Thu" },
  { n: 5, label: "Fri" },
  { n: 6, label: "Sat" },
  { n: 7, label: "Sun" },
];

function money(currency: string, amount: number) {
  return `${currency} ${Number(amount || 0).toFixed(2)}`;
}

function formatTime12h(t?: string | null) {
  if (!t) return "";
  const parts = String(t).split(":");
  const hh = Number(parts[0] || 0);
  const mm = Number(parts[1] || 0);
  const d = new Date(2000, 0, 1, hh, mm, 0);
  return format(d, "h:mm a");
}

function mapsUrl(args: { placeId?: string | null; address?: string | null; lat?: any; lng?: any }) {
  const placeId = args.placeId ? String(args.placeId) : "";
  if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
  const lat = args.lat != null ? Number(args.lat) : null;
  const lng = args.lng != null ? Number(args.lng) : null;
  if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }
  const address = args.address ? String(args.address) : "";
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return "";
}

export default function OrdersPublicPage() {
  const { businessSlug, linkSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [employeeName, setEmployeeName] = useState("");
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [doneId, setDoneId] = useState<string | null>(null);
  const [feedbackOn, setFeedbackOn] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const [dayChoice, setDayChoice] = useState<Record<number, { menu_item_id: string; quantity: number; notes: string }>>(
    () => ({})
  );

  useEffect(() => {
    const run = async () => {
      if (!businessSlug || !linkSlug) return;
      setLoading(true);
      const { data, error } = await (supabase as any).rpc("get_public_food_menu", {
        _business_slug: businessSlug,
        _link_slug: linkSlug,
      });
      if (error) {
        setData(null);
      } else {
        setData((data && data[0]) || null);
      }
      setLoading(false);
    };
    run();
  }, [businessSlug, linkSlug]);

  const itemsByDay = useMemo(() => {
    const items = (data?.items as any[]) || [];
    const map: Record<number, any[]> = {};
    for (const it of items) {
      const d = Number(it.day_of_week);
      if (!map[d]) map[d] = [];
      map[d].push(it);
    }
    return map;
  }, [data]);

  const visibleDays = useMemo(() => {
    const hasWeekend = (itemsByDay[6]?.length || 0) > 0 || (itemsByDay[7]?.length || 0) > 0;
    return DOW_ALL.filter((d) => d.n <= 5 || hasWeekend);
  }, [itemsByDay]);

  const currency = data?.currency || "GHS";

  const submit = async () => {
    if (!businessSlug || !linkSlug) return;
    const name = employeeName.trim();
    if (!name) return;
    const email = employeeEmail.trim();

    const lines = visibleDays.map((d) => {
      const v = dayChoice[d.n];
      if (!v?.menu_item_id) return null;
      return {
        day_of_week: d.n,
        menu_item_id: v.menu_item_id,
        quantity: Math.max(1, Number(v.quantity || 1)),
        notes: (v.notes || "").trim() || null,
      };
    }).filter(Boolean);

    if (lines.length === 0) return;

    setSubmitting(true);
    try {
      const { data: res, error } = await (supabase as any).rpc("submit_food_orders", {
        _business_slug: businessSlug,
        _link_slug: linkSlug,
        _employee_name: name,
        _employee_email: email || null,
        _lines: lines as any,
      });
      if (error) throw error;
      const sid = res?.[0]?.submission_id as string | undefined;
      setDoneId(sid || "ok");

      void sendOwnerPush({
        businessSlug,
        title: "New food order submission",
        body: `A new weekly order submission was placed by ${name}.`,
        url: "/app/orders",
        data: {
          context: "food_order_submission_created",
          context_id: sid || null,
        },
      });
    } catch (e: any) {
      alert(e?.message || "Failed to submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendFeedback = async () => {
    if (!businessSlug || !linkSlug) return;
    const msg = feedbackText.trim();
    if (!msg) return;
    setFeedbackSending(true);
    try {
      const { error } = await (supabase as any).rpc("submit_food_order_feedback", {
        _business_slug: businessSlug,
        _link_slug: linkSlug,
        _employee_name: employeeName.trim() || null,
        _message: msg,
      });
      if (error) throw error;
      setFeedbackSent(true);
      setFeedbackText("");
      setFeedbackOn(false);
    } catch (e: any) {
      alert(e?.message || "Failed to send feedback.");
    } finally {
      setFeedbackSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background p-6">
        <p className="text-sm text-muted-foreground">This menu link is not available.</p>
      </div>
    );
  }

  if (doneId) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="mx-auto max-w-xl">
          <Card>
            <CardContent className="p-6 space-y-2">
              <div className="text-lg font-semibold">Order submitted</div>
              <div className="text-sm text-muted-foreground">Thanks, {employeeName.trim()}.</div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="rounded-2xl border border-border bg-card p-6">
          {(() => {
            const ws = data.week_start_date ? parseISO(String(data.week_start_date)) : null;
            const we = ws ? addDays(ws, 6) : null;
            const weekRange = ws && we ? `${format(ws, "dd/MM/yyyy")} to ${format(we, "dd/MM/yyyy")}` : String(data.week_start_date);
            const url = mapsUrl({
              placeId: data.location_place_id,
              address: data.location_address,
              lat: data.location_lat,
              lng: data.location_lng,
            });
            return (
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  {data.business_logo_url ? (
                    <img
                      src={String(data.business_logo_url)}
                      alt={`${data.business_name} logo`}
                      className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl object-cover border border-border bg-background"
                    />
                  ) : (
                    <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl border border-border bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground">
                      {String(data.business_name || "B").slice(0, 1).toUpperCase()}
                    </div>
                  )}

                  <div className="min-w-0">
                    <div className="text-xl sm:text-2xl font-bold text-foreground truncate">{data.business_name}</div>
                    <div className="mt-1 text-base sm:text-lg font-semibold text-foreground">Weekly Menu</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {data.company_name} • {weekRange}
                    </div>
                    {(data.delivery_start_time || data.delivery_end_time) ? (
                      <div className="mt-2 text-sm text-muted-foreground">
                        Delivery window: {formatTime12h(data.delivery_start_time) || "—"} – {formatTime12h(data.delivery_end_time) || "—"}
                      </div>
                    ) : null}
                    {data.cutoff_time ? (
                      <div className="mt-1 text-sm text-muted-foreground">Cutoff: {formatTime12h(data.cutoff_time)}</div>
                    ) : null}
                  </div>
                </div>

                {url ? (
                  <div className="sm:pt-1">
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent/50 transition-colors w-full sm:w-auto"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Location
                    </a>
                  </div>
                ) : null}
              </div>
            );
          })()}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <div className="space-y-2">
            <Label>Your name</Label>
            <Input value={employeeName} onChange={(e) => setEmployeeName(e.target.value)} placeholder="e.g., Ama Mensah" />
          </div>
          <div className="space-y-2">
            <Label>Email (optional)</Label>
            <Input
              type="email"
              value={employeeEmail}
              onChange={(e) => setEmployeeEmail(e.target.value)}
              placeholder="e.g., ama@company.com"
            />
            <div className="text-xs text-muted-foreground">
              This helps the caterer recognize repeat orders and improve analytics.
            </div>
          </div>

          <div className="grid gap-3">
            {visibleDays.map((d) => {
              const options = itemsByDay[d.n] || [];
              const v = dayChoice[d.n] || { menu_item_id: "", quantity: 1, notes: "" };
              return (
                <div key={d.n} className="rounded-xl border border-border p-4">
                  <div className="font-medium">{d.label}</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-12">
                    <div className="sm:col-span-7">
                      <Label className="text-xs text-muted-foreground">Meal</Label>
                      <Select
                        value={v.menu_item_id}
                        onValueChange={(val) =>
                          setDayChoice((prev) => ({ ...prev, [d.n]: { ...v, menu_item_id: val } }))
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder={options.length ? "Select a meal" : "No meals for this day"} />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map((it: any) => (
                            <SelectItem key={it.id} value={it.id}>
                              {(() => {
                                const price = Number(it.price_amount || 0);
                                return (
                                  <>
                                    {it.name}
                                    {price > 0 ? ` • ${money(currency, price)}` : ""}
                                  </>
                                );
                              })()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Qty</Label>
                      <Input
                        className="mt-1"
                        type="number"
                        min={1}
                        value={v.quantity}
                        onChange={(e) =>
                          setDayChoice((prev) => ({
                            ...prev,
                            [d.n]: { ...v, quantity: Number(e.target.value) },
                          }))
                        }
                      />
                    </div>
                    <div className="sm:col-span-12">
                      <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
                      <Textarea
                        className="mt-1 min-h-[60px]"
                        value={v.notes}
                        onChange={(e) =>
                          setDayChoice((prev) => ({
                            ...prev,
                            [d.n]: { ...v, notes: e.target.value },
                          }))
                        }
                        placeholder="e.g., no spicy"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end">
            <Button onClick={submit} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit order"
              )}
            </Button>
          </div>

          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground">Feedback / report</div>
                <div className="text-xs text-muted-foreground">Help the vendor improve (or report an issue).</div>
              </div>
              <Switch checked={feedbackOn} onCheckedChange={(v) => setFeedbackOn(Boolean(v))} />
            </div>

            {feedbackSent ? (
              <div className="mt-3 text-sm text-muted-foreground">Thanks — your feedback has been sent.</div>
            ) : null}

            {feedbackOn ? (
              <div className="mt-3 space-y-2">
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="e.g., Portion was small, delivery was late, wrong meal listed, etc."
                  className="min-h-[90px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={sendFeedback}
                  disabled={feedbackSending || !feedbackText.trim()}
                >
                  {feedbackSending ? "Sending..." : "Send feedback"}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}


